/**
 * Hook: usePipelineStages
 *
 * CRUD de etapas (stages) de uma pipeline
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { ROLES } from '@/features/auth/types';
import { KanbanBoard, PipelineStage } from '../types';
import { pipelineBoardKey, pipelineStagesKey } from './queryKeys';

export const STAGE_COLOR_PRESETS = [
  '#7B68EE',
  '#4c8bf5',
  '#06b6d4',
  '#22c55e',
  '#f59e0b',
  '#eab308',
  '#ef4444',
  '#94a3b8',
];

const EMPTY_STAGES: PipelineStage[] = [];

/**
 * Roles autorizados a criar/editar/excluir/reordenar etapas.
 * Mantido em sincronia com as policies `pipeline_stages_insert_policy`,
 * `pipeline_stages_update_policy` e `pipeline_stages_delete_policy` no Supabase.
 */
const PIPELINE_STAGE_MANAGER_ROLES: string[] = [
  ROLES.SUPER_ADMIN,
  ROLES.ASSOCIADO,
  ROLES.SECRETARIA,
  ROLES.SUPERVISOR,
  ROLES.REPRESENTANTE,
];

export function canManagePipelineStages(role?: string): boolean {
  return !!role && PIPELINE_STAGE_MANAGER_ROLES.includes(role);
}

export const FORBIDDEN_STAGE_MESSAGE =
  '403 Forbidden: você não tem permissão para criar, editar ou excluir etapas desta pipeline.';

function stageErrorMessage(error: { message: string; code?: string }): string {
  if (error.code === '42501' || /row-level security/i.test(error.message)) {
    return FORBIDDEN_STAGE_MESSAGE;
  }
  return error.message;
}

export function usePipelineStages(pipelineId: string | null) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const canManage = canManagePipelineStages(profile?.role);

  const { data: stages = EMPTY_STAGES, isLoading, error, refetch } = useQuery({
    queryKey: pipelineStagesKey(pipelineId ?? ''),
    queryFn: async () => {
      if (!pipelineId) return [];
      return await fetchStages(pipelineId);
    },
    enabled: !!pipelineId,
    staleTime: 0,
  });

  const syncBoardCache = async () => {
    if (!pipelineId) return;
    await queryClient.invalidateQueries({
      queryKey: pipelineBoardKey(pipelineId),
      refetchType: 'active',
    });
  };

  const addStageToBoardCache = (stage: PipelineStage) => {
    if (!pipelineId) return;
    queryClient.setQueriesData<KanbanBoard>(
      { queryKey: pipelineBoardKey(pipelineId) },
      (old) => {
        if (!old) return old;
        if (old.stages.some((s) => s.stage.id === stage.id)) return old;
        return {
          ...old,
          stages: [...old.stages, { stage, cards: [], count: 0 }],
          lastUpdated: new Date(),
        };
      }
    );
  };

  const updateStageInBoardCache = (stage: PipelineStage) => {
    if (!pipelineId) return;
    queryClient.setQueriesData<KanbanBoard>(
      { queryKey: pipelineBoardKey(pipelineId) },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          stages: old.stages.map((swc) =>
            swc.stage.id === stage.id ? { ...swc, stage: { ...swc.stage, ...stage } } : swc
          ),
          lastUpdated: new Date(),
        };
      }
    );
  };

  const removeStageFromBoardCache = (stageId: string) => {
    if (!pipelineId) return;
    queryClient.setQueriesData<KanbanBoard>(
      { queryKey: pipelineBoardKey(pipelineId) },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          stages: old.stages.filter((swc) => swc.stage.id !== stageId),
          totalCards: old.stages
            .filter((swc) => swc.stage.id !== stageId)
            .reduce((sum, swc) => sum + swc.count, 0),
          lastUpdated: new Date(),
        };
      }
    );
  };

  const invalidateStagesList = () => {
    if (!pipelineId) return;
    queryClient.invalidateQueries({ queryKey: pipelineStagesKey(pipelineId) });
  };

  const createStage = useMutation({
    mutationFn: async (input: { nome: string; cor?: string }) => {
      if (!canManage) throw new Error(FORBIDDEN_STAGE_MESSAGE);
      if (!pipelineId) throw new Error('Pipeline não selecionada');
      return await createStageRecord(pipelineId, input.nome, input.cor);
    },
    onSuccess: async (newStage) => {
      addStageToBoardCache(newStage);
      invalidateStagesList();
      await syncBoardCache();
    },
  });

  const updateStage = useMutation({
    mutationFn: async (input: { id: string; nome?: string; cor?: string }) => {
      if (!canManage) throw new Error(FORBIDDEN_STAGE_MESSAGE);

      const payload: Record<string, string> = {};
      if (input.nome !== undefined) payload.nome = input.nome;
      if (input.cor !== undefined) payload.cor = input.cor;

      const { data, error: updateError } = await supabase
        .from('pipeline_stages')
        .update(payload)
        .eq('id', input.id)
        .select()
        .single();

      if (updateError) throw new Error(stageErrorMessage(updateError));
      return {
        ...data,
        campos_obrigatorios: data.campos_obrigatorios as string[] | undefined,
        visibilidade: data.visibilidade as any,
      } as PipelineStage;
    },
    onSuccess: async (updatedStage) => {
      updateStageInBoardCache(updatedStage);
      invalidateStagesList();
      await syncBoardCache();
    },
  });

  const deleteStage = useMutation({
    mutationFn: async (stageId: string) => {
      if (!canManage) throw new Error(FORBIDDEN_STAGE_MESSAGE);

      if (stages.length <= 1 && pipelineId) {
        const { data: pipelineRow, error: pipelineError } = await supabase
          .from('pipelines')
          .select('fixa')
          .eq('id', pipelineId)
          .single();

        if (pipelineError) throw new Error(stageErrorMessage(pipelineError));
        if (pipelineRow?.fixa) {
          throw new Error('Uma pipeline fixa precisa ter ao menos 1 etapa.');
        }
      }

      const { count, error: countError } = await supabase
        .from('pipeline_cards')
        .select('id', { count: 'exact', head: true })
        .eq('stage_id', stageId)
        .eq('cancelado', false);

      if (countError) throw new Error(stageErrorMessage(countError));
      if (count && count > 0) {
        throw new Error(`Esta etapa possui ${count} card(s). Mova ou remova antes de excluir.`);
      }

      const { error: deleteError } = await supabase
        .from('pipeline_stages')
        .delete()
        .eq('id', stageId);

      if (deleteError) throw new Error(stageErrorMessage(deleteError));
      return stageId;
    },
    onSuccess: async (stageId) => {
      removeStageFromBoardCache(stageId);
      invalidateStagesList();
      await syncBoardCache();
    },
  });

  const reorderStages = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      if (!canManage) throw new Error(FORBIDDEN_STAGE_MESSAGE);
      if (!pipelineId) throw new Error('Pipeline não selecionada');
      await reorderStagesRecord(pipelineId, orderedIds);
    },
    onSuccess: async () => {
      invalidateStagesList();
      await syncBoardCache();
    },
  });

  return {
    stages,
    loading: isLoading,
    error: error as Error | null,
    refetch,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
  };
}

async function fetchStages(pipelineId: string): Promise<PipelineStage[]> {
  const { data, error } = await supabase
    .from('pipeline_stages')
    .select('*')
    .eq('pipeline_id', pipelineId)
    .order('ordem', { ascending: true });

  if (error) throw new Error(error.message);
  return (data || []).map(stage => ({
    ...stage,
    campos_obrigatorios: stage.campos_obrigatorios as string[] | undefined,
    visibilidade: stage.visibilidade as any,
  } as PipelineStage));
}

async function createStageRecord(
  pipelineId: string,
  nome: string,
  cor?: string
): Promise<PipelineStage> {
  const { data: existing } = await supabase
    .from('pipeline_stages')
    .select('ordem')
    .eq('pipeline_id', pipelineId)
    .order('ordem', { ascending: false })
    .limit(1);

  const nextOrdem = (existing?.[0]?.ordem ?? 0) + 1;
  const colorIndex = (nextOrdem - 1) % STAGE_COLOR_PRESETS.length;

  const { data, error } = await supabase
    .from('pipeline_stages')
    .insert({
      pipeline_id: pipelineId,
      nome: nome.trim(),
      ordem: nextOrdem,
      cor: cor || STAGE_COLOR_PRESETS[colorIndex],
      visibilidade: 'publico',
    })
    .select()
    .single();

  if (error) throw new Error(stageErrorMessage(error));
  return {
    ...data,
    campos_obrigatorios: data.campos_obrigatorios as string[] | undefined,
    visibilidade: data.visibilidade as any,
  } as PipelineStage;
}

async function reorderStagesRecord(
  pipelineId: string,
  orderedIds: string[]
): Promise<void> {
  const stages = await fetchStages(pipelineId);
  const currentOrdem = new Map(stages.map((s) => [s.id, s.ordem]));

  const changed = orderedIds
    .map((id, index) => ({ id, ordem: index + 1 }))
    .filter(({ id, ordem }) => currentOrdem.get(id) !== ordem);

  for (const { id, ordem } of changed) {
    const { error } = await supabase
      .from('pipeline_stages')
      .update({ ordem })
      .eq('id', id);

    if (error) throw new Error(stageErrorMessage(error));
  }
}
